/**
 * @module truncate
 */
const { DELIMITERS } = require('../constants');

/**
 * @typedef {object} TruncateOptions
 * @property {boolean} breakAtDelimiter - Boolean flag denoting whether or not to break at one of the specified delimiter characters.
 * @property {boolean} breakAtSpace - Boolean flag denoting whether or not to break at a space. Note that this value trumps that of the breakAtDelimiter value.
 * @property {Array} delimiters - Array of delimiter characters at which to break. (Default: ['.', '?', '!', ',', ';', ':', '—', '–', '-']).
 */

/**
 * Convenience function to truncate the specified string to the maximum length specified, using optional configuration options when calculating the return value.
 *
 * @param {string} str - The source string to truncate.
 * @param {number} limit - The maximum length of the string returned with ellipsis when truncated.
 * @param {TruncateOptions} options - Optional configuration settings to fine-tune the truncated result.
 *
 * @returns {string} The truncated string, with ellipsis at the end when source string truncated. If no string specified, returns a blank string.
 */
function truncate(
  str,
  limit,
  options = {
    breakAtDelimiter: false,
    breakAtSpace: true,
    delimiters: DELIMITERS,
  },
) {
  if (!str) {
    return str || '';
  }
  if (!limit || str.length <= limit) {
    return str;
  }
  const charAtLimit = str.charAt(limit);
  let newStr = str.substring(0, limit);
  if (
    options &&
    options.breakAtDelimiter &&
    Array.isArray(options.delimiters) &&
    !options.delimiters.includes(charAtLimit)
  ) {
    let lastDelimiterIndex = 0;
    options.delimiters.forEach((delimiter) => {
      if (newStr.lastIndexOf(delimiter) > lastDelimiterIndex) {
        lastDelimiterIndex = newStr.lastIndexOf(delimiter);
      }
    });
    newStr = newStr.substring(0, lastDelimiterIndex + 1);
  } else if (options && options.breakAtSpace && charAtLimit !== ' ') {
    newStr = newStr.substring(0, newStr.lastIndexOf(' '));
  }
  return `${newStr}...`;
}

module.exports = {
  truncate,
};
